// ═══════════════════════════════════════════════════════════════════════════
// Block authoring DXF parser/writer helpers (Phase K.5)
// ═══════════════════════════════════════════════════════════════════════════
//
// Group-list helpers tuned for AutoCAD's BLOCK<KIND>PARAMETER /
// BLOCK<KIND>ACTION subclass-segmented DXF dump format. The fixture
// data has the shape:
//
//   100 AcDbEvalExpr
//     90 nodeId
//     98 ...
//     99 ...
//   100 AcDbBlockElement
//     300 displayName
//     1071 paletteShow
//   100 AcDbBlock<XPt>Parameter
//     1010/1020/1030 anchor
//     ...
//   100 AcDbBlock<Type>Parameter
//     <type-specific groups>
//
// The same group code may carry different semantics under different
// subclass markers (e.g. `91` in AcDbEvalExpr is the eval-expr id,
// `91` in AcDbBlockGripExpr is the input parameter id). All subclass-
// aware parsers MUST partition groups by subclass first, then look up
// codes within the relevant subclass slice.

import { decodeDxfUnicode, type DxfGroup } from "@dxf-ai-editor/dxf-io";

import type { AuthoringPoint, AuthoringValueSet } from "../blockAuthoring";

/**
 * Partition the raw group list by the AcDb subclass markers (group
 * code 100). Returns a map from subclass name to the slice of groups
 * (excluding the marker itself) that belong to it.
 *
 * Subclass markers seen in fixtures:
 *   AcDbEvalExpr
 *   AcDbBlockElement
 *   AcDbBlockParameter
 *   AcDbBlock1PtParameter / AcDbBlock2PtParameter
 *   AcDbBlock<Type>Parameter (Linear / Flip / Visibility / …)
 *   AcDbBlockGrip
 *   AcDbBlockGripExpr
 *   AcDbBlockAction
 *   AcDbBlockActionWithBasePt
 *   AcDbBlock<Type>Action
 *
 * Note: a single object may have BOTH AcDbBlock1PtParameter AND
 * AcDbBlock<Type>Parameter; both slices are populated.
 */
export function partitionSubclasses(
  groups: DxfGroup[],
): Map<string, DxfGroup[]> {
  const out = new Map<string, DxfGroup[]>();
  let current: string | null = null;
  for (const g of groups) {
    if (g.code === 100) {
      current = g.value.trim();
      if (!out.has(current)) out.set(current, []);
      continue;
    }
    if (current === null) continue;
    out.get(current)!.push(g);
  }
  return out;
}

/**
 * First group with the given code in a slice; returns undefined when
 * absent.
 *
 * T9 PR-3 — decodes the CONSUMER view (`\U+XXXX` → real char). The block-
 * authoring OBJECTS re-emit through the dxf-io writer chokepoint, which escapes
 * string-content codes (300/301/302/303/304/305/306/307…), so a Cyrillic
 * parameter name / label / lookup string would otherwise re-import as a literal
 * `\U+…`. Decoding here is by-construction complete for every name/label/string
 * read and a guaranteed NO-OP on the handle / numeric values this helper also
 * serves (`decodeDxfUnicode` returns its input when no `\U+` is present). The
 * raw `g.value` is never mutated, so verbatim re-export stays byte-identical.
 */
export function firstGroup(
  groups: DxfGroup[],
  code: number,
): string | undefined {
  for (const g of groups) {
    if (g.code === code) return decodeDxfUnicode(g.value);
  }
  return undefined;
}

/**
 * First group's value parsed as a finite number; undefined when
 * absent or non-numeric.
 */
export function firstNumber(
  groups: DxfGroup[],
  code: number,
): number | undefined {
  const raw = firstGroup(groups, code);
  if (raw === undefined) return undefined;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * First group's value as integer; undefined when absent or non-numeric.
 */
export function firstInt(groups: DxfGroup[], code: number): number | undefined {
  const raw = firstGroup(groups, code);
  if (raw === undefined) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * All values for a given group code, in order, as strings.
 * T9 PR-3 — decodes the CONSUMER view (see firstGroup). No-op on handle /
 * numeric values; raw `g.value` untouched so re-export stays byte-identical.
 */
export function allGroups(groups: DxfGroup[], code: number): string[] {
  const out: string[] = [];
  for (const g of groups) {
    if (g.code === code) out.push(decodeDxfUnicode(g.value));
  }
  return out;
}

/**
 * All values for a given group code, parsed as finite numbers.
 */
export function allNumbers(groups: DxfGroup[], code: number): number[] {
  const out: number[] = [];
  for (const g of groups) {
    if (g.code === code) {
      const n = Number.parseFloat(g.value);
      if (Number.isFinite(n)) out.push(n);
    }
  }
  return out;
}

/**
 * All values for a given group code, parsed as integers.
 *
 * Reserved for future visibility / lookup table parsing where multiple
 * integer fields share the same group code (e.g. Tier-N future kinds
 * with sparse axis-flag arrays). Underscore prefix marks it as
 * deliberately unused for the current Phase K.5 surface.
 */
export function allInts(groups: DxfGroup[], code: number): number[] {
  const out: number[] = [];
  for (const g of groups) {
    if (g.code === code) {
      const n = Number.parseInt(g.value, 10);
      if (Number.isFinite(n)) out.push(n);
    }
  }
  return out;
}

/**
 * Parse a 3D point from xCode/yCode/zCode group codes (default
 * 1010/1020/1030 for primary point, 1011/1021/1031 for secondary, etc.).
 */
export function parsePoint(
  groups: DxfGroup[],
  xCode: number,
  yCode: number,
  zCode: number,
): AuthoringPoint {
  return {
    x: firstNumber(groups, xCode) ?? 0,
    y: firstNumber(groups, yCode) ?? 0,
    z: firstNumber(groups, zCode) ?? 0,
  };
}

/**
 * Look up a value at the Nth occurrence of a code in a group list.
 * Returns undefined when fewer than `n+1` occurrences exist.
 */
export function nthGroup(
  groups: DxfGroup[],
  code: number,
  n: number,
): string | undefined {
  let count = 0;
  for (const g of groups) {
    if (g.code === code) {
      if (count === n) return g.value;
      count += 1;
    }
  }
  return undefined;
}

export function nthNumber(
  groups: DxfGroup[],
  code: number,
  n: number,
): number | undefined {
  const raw = nthGroup(groups, code, n);
  if (raw === undefined) return undefined;
  const v = Number.parseFloat(raw);
  return Number.isFinite(v) ? v : undefined;
}

/**
 * Decode a value-set block from a parameter's type-specific subclass
 * slice. AutoCAD's value-set fields aren't perfectly standardised
 * across parameter kinds; the caller passes the relevant code map.
 *
 * Mode encoding:
 *   175 = number of value-set list items (used as the discriminator:
 *         0 = "none" or "increment", >0 = "list").
 *   141/142/143 = min/max/increment for "increment" mode.
 *   144 (repeated) = list values for "list" mode.
 */
export function parseLinearValueSet(groups: DxfGroup[]): AuthoringValueSet {
  const numItems = firstInt(groups, 175) ?? 0;
  const min = firstNumber(groups, 141) ?? 0;
  const max = firstNumber(groups, 142) ?? 0;
  const increment = firstNumber(groups, 143) ?? 0;
  const listValues = allNumbers(groups, 144);

  let mode: AuthoringValueSet["mode"] = "none";
  if (numItems > 0 || listValues.length > 0) {
    mode = "list";
  } else if (increment !== 0 || min !== 0 || max !== 0) {
    mode = "increment";
  }

  return {
    mode,
    min,
    max,
    increment,
    listValues,
  };
}

// ─── Writer helpers ──────────────────────────────────────────────────

/**
 * Push a group onto an accumulator using AutoCAD's float-format
 * convention (matches existing dxfBridge.dynamic.shared.pushGroup):
 *   - Integer values emitted as decimal
 *   - Float values formatted with toFixed(12) and trailing zeros
 *     trimmed; "-0" normalised to "0".
 *   - Non-finite numbers silently skipped (would corrupt DXF).
 */
export function pushGroup(
  acc: DxfGroup[],
  code: number,
  value: string | number,
): void {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return;
    if (Number.isInteger(value)) {
      acc.push({ code, value: String(value) });
    } else {
      const fixed = value.toFixed(12).replace(/\.?0+$/, "");
      acc.push({ code, value: fixed === "-0" ? "0" : fixed });
    }
    return;
  }
  acc.push({ code, value });
}

/**
 * Push a 3D point on the supplied codes (e.g. 1010/1020/1030).
 */
export function pushPoint(
  acc: DxfGroup[],
  point: AuthoringPoint,
  xCode: number,
  yCode: number,
  zCode: number,
): void {
  pushGroup(acc, xCode, point.x);
  pushGroup(acc, yCode, point.y);
  pushGroup(acc, zCode, point.z);
}

/**
 * Emit a value-set block on a Linear-style parameter. Inverse of
 * `parseLinearValueSet`. Always emits 141/142/143/175 (even when zero)
 * for round-trip consistency; emits 144 entries only when mode === "list".
 */
export function pushLinearValueSet(
  acc: DxfGroup[],
  vs: AuthoringValueSet,
): void {
  pushGroup(acc, 141, vs.min);
  pushGroup(acc, 142, vs.max);
  pushGroup(acc, 143, vs.increment);
  pushGroup(acc, 175, vs.mode === "list" ? vs.listValues.length : 0);
  if (vs.mode === "list") {
    for (const v of vs.listValues) {
      pushGroup(acc, 144, v);
    }
  }
}

/**
 * Emit a subclass marker (group 100).
 */
export function pushSubclass(acc: DxfGroup[], name: string): void {
  acc.push({ code: 100, value: name });
}
